import type { MouseEvent } from "react";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import {
    Box,
    Typography,
    Divider,
    Collapse,
    Button,
    useTheme,
    Avatar,
    Menu,
    MenuItem,
    Popover,
    Fade,
} from "@mui/material";
import { Post } from "../../types/post.types";
import { useGetPostComments, useDeleteComment, Comment } from "../../../../api/client-api/social/useComments";
import { useToggleCommentReaction } from "../../../../api/client-api/social/useCommentReactions";
import { CompactCommentForm, CompactCommentFormHandle } from "./CompactCommentForm";
import { useTranslation } from "react-i18next";
import { FoodsIconButton, FoodsTooltip } from "@foodpilot/foods";
import { ReactionIcon } from "../ReactionIcon";
import { REACTION_TYPES, getReactionConfig } from "../../types/reaction.types";
import { formatRelativeTime } from "../../utils/formatRelativeTime";
import { SocialContentLanguageMode } from "src/api/client-api/social/useSocialContentLanguagePreference";
import { resolveSocialDisplayContent } from "../../utils/resolveSocialDisplayContent";

interface FacebookStyleCommentsProps {
    post: Post;
    contentLanguageMode: SocialContentLanguageMode;
    onCommentAdded?: () => void;
}

export type FacebookStyleCommentsHandle = {
    focusCommentInput: () => void;
};

interface CompactCommentItemProps {
    comment: Comment;
    postId: number;
    contentLanguageMode: SocialContentLanguageMode;
    level?: number;
    onReply?: () => void;
}

// Comment reactions reuse post reaction config from FOODS icons
type CommentReactionType = (typeof REACTION_TYPES)[number]["type"];

const CompactCommentItem = ({ comment, postId, contentLanguageMode, level = 0, onReply }: CompactCommentItemProps) => {
    const theme = useTheme();
    const [showReplyForm, setShowReplyForm] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [reactionAnchorEl, setReactionAnchorEl] = useState<null | HTMLElement>(null);
    const deleteComment = useDeleteComment();
    const toggleCommentReaction = useToggleCommentReaction();
    const { i18n, t } = useTranslation();
    const showReactionPicker = Boolean(reactionAnchorEl);

    // Get user reaction from comment data (assuming it's included in the API response)
    const userReaction = comment.userReaction || null;

    const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleDelete = async () => {
        if (window.confirm(t("social.comments.delete_confirmation", "Are you sure you want to delete this comment?"))) {
            try {
                await deleteComment.mutateAsync(comment.id);
            } catch (error) {
                // Error handled by mutation
            }
        }
        handleMenuClose();
    };

    const handleReply = () => {
        setShowReplyForm(!showReplyForm);
        onReply?.();
    };

    const handleReactionHover = (event: MouseEvent<HTMLElement>) => {
        setReactionAnchorEl(event.currentTarget);
    };

    const handleReactionClose = () => {
        setReactionAnchorEl(null);
    };

    const handleReactionSelect = async (type: CommentReactionType) => {
        try {
            await toggleCommentReaction.mutateAsync({
                commentId: comment.id,
                type: type,
            });
        } catch (error) {
            // Error handled by mutation
        }
        handleReactionClose();
    };

    const handleLikeClick = () => {
        handleReactionSelect("like");
    };

    const hasReaction = !!userReaction;
    const likeReaction = getReactionConfig("like");
    const activeReaction = getReactionConfig(userReaction ?? "");

    const maxLevel = 2; // Limit nesting for Facebook style
    const isNested = level > 0;
    const canNest = level < maxLevel;
    const displayedCommentContent = resolveSocialDisplayContent(comment, contentLanguageMode);

    return (
        <Box sx={{ mb: 1 }}>
            <Box
                display="flex"
                gap={1}
                sx={{
                    ml: isNested ? 4 : 0,
                }}
            >
                <Avatar
                    src={comment.author.avatarUrl}
                    sx={{
                        width: 28,
                        height: 28,
                        fontSize: "0.75rem",
                    }}
                >
                    {comment.author.firstname?.[0]}
                    {comment.author.lastname?.[0]}
                </Avatar>

                <Box flex={1}>
                    <Box
                        sx={{
                            backgroundColor: theme.custom.grey[100],
                            borderRadius: 3,
                            px: 2,
                            py: 1,
                            position: "relative",
                            display: "inline-block",
                            maxWidth: "100%",
                        }}
                    >
                        <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: "0.875rem", mb: 0.5 }}>
                            {comment.author.firstname} {comment.author.lastname}
                        </Typography>
                        {!!comment.author.organizationNames?.length && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", fontSize: "0.7rem", mb: 0.5 }}
                            >
                                {comment.author.organizationNames.join(", ")}
                            </Typography>
                        )}
                        <Typography variant="body2" sx={{ fontSize: "0.875rem", wordBreak: "break-word" }}>
                            {displayedCommentContent}
                        </Typography>

                        {/* Comment Reaction Summary */}
                        {comment.reactionsSummary && Object.keys(comment.reactionsSummary).length > 0 && (
                            <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                                {Object.entries(comment.reactionsSummary).map(([type, count]) => {
                                    const reactionConfig = getReactionConfig(type);
                                    if (!reactionConfig) return null;

                                    return (
                                        <Box
                                            key={type}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.25,
                                                backgroundColor: theme.palette.grey[200],
                                                borderRadius: "12px",
                                                px: 0.75,
                                                py: 0.25,
                                            }}
                                        >
                                            <ReactionIcon config={reactionConfig} size={1} />
                                            <Typography variant="caption" sx={{ fontSize: "0.7rem", fontWeight: 500 }}>
                                                {count}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}

                        {(comment.canModify || comment.canDelete) && (
                            <FoodsIconButton
                                size="xs"
                                icon="details"
                                buttonProps={{
                                    onClick: handleMenuOpen,
                                    sx: {
                                        position: "absolute",
                                        top: 4,
                                        right: 4,
                                    },
                                }}
                            />
                        )}
                    </Box>

                    <Box display="flex" alignItems="center" gap={2} sx={{ mt: 0.5, ml: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                            {formatRelativeTime(new Date(comment.createdAt), i18n.resolvedLanguage ?? i18n.language)}
                        </Typography>

                        {/* LinkedIn-style Like button with hover reactions */}
                        <Box sx={{ position: "relative" }}>
                            <Button
                                size="small"
                                onClick={handleLikeClick}
                                onMouseEnter={handleReactionHover}
                                sx={{
                                    textTransform: "none",
                                    color:
                                        hasReaction ? activeReaction?.color || likeReaction?.color : "text.secondary",
                                    minWidth: "auto",
                                    p: 0,
                                    fontSize: "0.75rem",
                                    fontWeight: hasReaction ? 700 : 600,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    "&:hover": {
                                        backgroundColor: "transparent",
                                        textDecoration: "underline",
                                    },
                                }}
                            >
                                <ReactionIcon
                                    config={hasReaction && activeReaction ? activeReaction : { icon: "starEmpty" }}
                                    size={1}
                                />
                                {t("social.comments.like", "Like")}
                            </Button>

                            {/* Comment Reaction Picker Popover */}
                            <Popover
                                open={showReactionPicker}
                                anchorEl={reactionAnchorEl}
                                onClose={handleReactionClose}
                                anchorOrigin={{
                                    vertical: "top",
                                    horizontal: "left",
                                }}
                                transformOrigin={{
                                    vertical: "bottom",
                                    horizontal: "left",
                                }}
                                TransitionComponent={Fade}
                                TransitionProps={{ timeout: 200 }}
                                sx={{
                                    "& .MuiPopover-paper": {
                                        borderRadius: "20px",
                                        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                                        border: `1px solid ${theme.custom.grey[300]}`,
                                        overflow: "visible",
                                        mt: -0.5,
                                    },
                                }}
                                onMouseLeave={handleReactionClose}
                            >
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    sx={{
                                        p: 0.5,
                                        gap: 0.25,
                                    }}
                                    onMouseLeave={handleReactionClose}
                                >
                                    {REACTION_TYPES.map((reaction) => {
                                        const isSelected = userReaction === reaction.type;

                                        return (
                                            <FoodsTooltip
                                                key={reaction.type}
                                                title={t(reaction.labelKey, reaction.defaultLabel)}
                                            >
                                                <Box
                                                    component="button"
                                                    type="button"
                                                    onClick={() =>
                                                        handleReactionSelect(reaction.type as CommentReactionType)
                                                    }
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        borderRadius: "50%",
                                                        width: 32,
                                                        height: 32,
                                                        border: "none",
                                                        cursor: "pointer",
                                                        backgroundColor:
                                                            isSelected ? `${reaction.color}20` : "transparent",
                                                        outline:
                                                            isSelected ?
                                                                `1px solid ${reaction.color}`
                                                            :   "1px solid transparent",
                                                        "&:hover": {
                                                            backgroundColor: `${reaction.color}20`,
                                                            transform: "scale(1.15)",
                                                            outline: `1px solid ${reaction.color}`,
                                                        },
                                                        transition: "all 0.15s ease",
                                                    }}
                                                >
                                                    <ReactionIcon config={reaction} size={1.5} />
                                                </Box>
                                            </FoodsTooltip>
                                        );
                                    })}
                                </Box>
                            </Popover>
                        </Box>

                        {canNest && (
                            <Button
                                size="small"
                                onClick={handleReply}
                                sx={{
                                    textTransform: "none",
                                    color: "text.secondary",
                                    minWidth: "auto",
                                    p: 0,
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    "&:hover": {
                                        backgroundColor: "transparent",
                                        textDecoration: "underline",
                                    },
                                }}
                            >
                                {t("social.comments.reply", "Reply")}
                            </Button>
                        )}
                    </Box>

                    {showReplyForm && (
                        <Box sx={{ mt: 1, mb: 1 }}>
                            <CompactCommentForm
                                postId={postId}
                                parentCommentId={comment.id}
                                placeholder={t("social.comments.reply_to", "Reply to {name}...", {
                                    name: comment.author.firstname,
                                })}
                                onCancel={() => setShowReplyForm(false)}
                                onSuccess={() => setShowReplyForm(false)}
                            />
                        </Box>
                    )}

                    {/* Render replies */}
                    {comment.replies && comment.replies.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                            {comment.replies.map((reply) => (
                                <CompactCommentItem
                                    key={reply.id}
                                    comment={reply}
                                    postId={postId}
                                    contentLanguageMode={contentLanguageMode}
                                    level={level + 1}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Comment actions menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: { minWidth: 120 },
                }}
            >
                {comment.canModify && (
                    <MenuItem onClick={handleMenuClose} sx={{ fontSize: "0.875rem" }}>
                        {t("social.comments.edit", "Edit")}
                    </MenuItem>
                )}
                {comment.canDelete && (
                    <MenuItem onClick={handleDelete} sx={{ color: "error.main", fontSize: "0.875rem" }}>
                        {t("social.comments.delete", "Delete")}
                    </MenuItem>
                )}
            </Menu>
        </Box>
    );
};

export const FacebookStyleComments = forwardRef<FacebookStyleCommentsHandle, FacebookStyleCommentsProps>(
    function FacebookStyleComments({ post, contentLanguageMode, onCommentAdded }, ref) {
        const theme = useTheme();
        const [showComments, setShowComments] = useState(false);
        const [showCommentForm, setShowCommentForm] = useState(false);
        const commentFormRef = useRef<CompactCommentFormHandle>(null);
        const { data: comments, isLoading } = useGetPostComments(post.id, showComments);
        const { t } = useTranslation();
        // Filter top-level comments (not replies)
        const topLevelComments = (comments ?? []).filter((comment) => !comment.parentComment);
        const commentCount = comments === undefined ? post.commentCount : comments.length;

        const handleToggleComments = () => {
            setShowComments(!showComments);
            if (!showComments && !showCommentForm) {
                setShowCommentForm(true);
            }
        };

        const handleCommentAdded = () => {
            onCommentAdded?.();
            if (!showComments) {
                setShowComments(true);
            }
        };

        useImperativeHandle(ref, () => ({
            focusCommentInput: () => {
                setShowCommentForm(true);
                setShowComments(true);
                window.setTimeout(() => commentFormRef.current?.focus(), 0);
            },
        }));

        return (
            <Box>
                {/* Comment count and actions */}
                {commentCount > 0 && (
                    <Box sx={{ px: 2, py: 1 }}>
                        <Button
                            size="small"
                            onClick={handleToggleComments}
                            sx={{
                                textTransform: "none",
                                color: "text.secondary",
                                fontSize: "0.875rem",
                                fontWeight: 500,
                                p: 0,
                                "&:hover": {
                                    backgroundColor: "transparent",
                                    textDecoration: "underline",
                                },
                            }}
                        >
                            {showComments ? t("social.comments.hide", "Hide") : t("social.comments.view", "View")}{" "}
                            {commentCount}{" "}
                            {commentCount > 1 ?
                                t("social.comments.comments", "comments")
                            :   t("social.comments.comment", "comment")}
                        </Button>
                    </Box>
                )}

                {/* Divider */}
                {(showComments || showCommentForm) && <Divider sx={{ mx: 2 }} />}

                {/* Comments section */}
                <Collapse in={showComments} timeout={300}>
                    <Box sx={{ px: 2, py: 1 }}>
                        {isLoading ?
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                                {t("social.comments.loading", "Loading comments...")}
                            </Typography>
                        : topLevelComments.length > 0 ?
                            <Box>
                                {topLevelComments.map((comment) => (
                                    <CompactCommentItem
                                        key={comment.id}
                                        comment={comment}
                                        postId={post.id}
                                        contentLanguageMode={contentLanguageMode}
                                        level={0}
                                    />
                                ))}
                            </Box>
                        :   <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ textAlign: "center", py: 1, fontSize: "0.875rem" }}
                            >
                                {t("social.comments.be_the_first_to_comment", "Be the first to comment")}
                            </Typography>
                        }
                    </Box>
                </Collapse>

                {/* Comment form */}
                <Collapse in={showCommentForm || showComments} timeout={300}>
                    <Box sx={{ px: 2, pb: 2 }}>
                        <CompactCommentForm
                            ref={commentFormRef}
                            postId={post.id}
                            placeholder={t("social.comments.write_a_comment", "Write a comment...")}
                            onSuccess={handleCommentAdded}
                        />
                    </Box>
                </Collapse>

                {/* Show comment form button */}
                {!showCommentForm && !showComments && (
                    <Box sx={{ px: 2, pb: 1 }}>
                        <Button
                            size="small"
                            onClick={() => setShowCommentForm(true)}
                            sx={{
                                textTransform: "none",
                                color: "text.secondary",
                                fontSize: "0.875rem",
                                fontWeight: 500,
                                p: 0,
                                "&:hover": {
                                    backgroundColor: "transparent",
                                    textDecoration: "underline",
                                },
                            }}
                        >
                            {t("social.comments.write_a_comment", "Write a comment...")}
                        </Button>
                    </Box>
                )}
            </Box>
        );
    },
);
